import { useState, useCallback } from "react";

export interface SavedPrompt {
  id: string;
  prompt: string;
  classTitle: string;
  classId: number;
  savedAt: string;
  category: string;
}

const STORAGE_KEY = "growseo_prompts";

function extractCategory(prompt: string): string {
  if (/--v|midjourney|kawaii|sticker|illustration/i.test(prompt)) return "이미지 생성";
  if (/act as|email|report|summarize|업무|자동화/i.test(prompt)) return "업무 자동화";
  if (/script|youtube|스크립트|hook|aida/i.test(prompt)) return "콘텐츠 글쓰기";
  if (/chatbot|system prompt|customer service/i.test(prompt)) return "챗봇 개발";
  if (/caption|copy|marketing|카피/i.test(prompt)) return "카피라이팅";
  return "프롬프트";
}

function load(): SavedPrompt[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as SavedPrompt[]) : [];
  } catch {
    return [];
  }
}

function persist(prompts: SavedPrompt[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prompts));
  } catch (e) {
    console.warn("PromptStore save error", e);
  }
}

export function usePromptStore() {
  const [prompts, setPrompts] = useState<SavedPrompt[]>(load);

  const savePrompt = useCallback((prompt: string, classTitle: string, classId: number): string => {
    const newItem: SavedPrompt = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      prompt,
      classTitle,
      classId,
      savedAt: new Date().toLocaleDateString("ko-KR", { month: "short", day: "numeric" }),
      category: extractCategory(prompt),
    };
    setPrompts((prev) => {
      const next = [newItem, ...prev];
      persist(next);
      return next;
    });
    return newItem.id;
  }, []);

  const deletePrompt = useCallback((id: string) => {
    setPrompts((prev) => {
      const next = prev.filter((p) => p.id !== id);
      persist(next);
      return next;
    });
  }, []);

  const isPromptSaved = useCallback(
    (prompt: string) => prompts.some((p) => p.prompt === prompt),
    [prompts]
  );

  return { prompts, savePrompt, deletePrompt, isPromptSaved };
}
